<template>
  <div class="tx-list-page">
    <v-container fluid grid-list-xl>
      <v-layout row wrap>
        <v-flex xs12>
          <!-- Dos estados de cuenta por cliente y mes, nunca mezclados:
               "Reembolso de gastos operativos" (impuestos y lo que Transit
               adelantó por su cuenta) y "Servicios aduanales". Son los nombres
               que ve el cliente en el documento y en el correo, así que la
               pantalla los llama igual. El tipo, el mes y el cliente mandan
               sobre las dos tarjetas: lo pendiente y lo emitido. -->
          <div class="list-hero ec-hero">
            <div>
              <p class="list-crumb">Contabilidad / Estado de Cuenta</p>
              <h1 class="list-title">Estado de Cuenta</h1>
              <p class="list-subtitle">
                Dos estados de cuenta por cliente y mes:
                <strong>Reembolso de gastos operativos</strong> (impuestos,
                notaría, almacenaje, APA, bomberos…) y
                <strong>Servicios aduanales</strong>. Cada línea es una factura
                de Alegra.
              </p>
            </div>
            <div
              class="ec-toggle ec-toggle--kind"
              role="group"
              aria-label="Tipo de estado de cuenta"
            >
              <button
                v-for="opt in kinds"
                :key="opt.value"
                class="ec-toggle__btn"
                :class="{ 'ec-toggle__btn--on': kind === opt.value }"
                @click="switchKind(opt.value)"
              >
                {{ opt.label }}
              </button>
            </div>
          </div>

          <!-- Alcance de la pantalla: filtra lo pendiente y lo emitido. El mes
               es el de la factura Alegra; vacío = todos los meses. -->
          <div class="ec-scope">
            <label class="ec-month">
              <span class="ec-month__label">Mes</span>
              <input
                v-model="period"
                type="month"
                class="ec-month__input"
                @change="onPeriodChange"
              />
            </label>
            <div class="ec-toggle" role="group" aria-label="Tipo de cliente">
              <button
                v-for="opt in clientTypes"
                :key="opt.value"
                class="ec-toggle__btn"
                :class="{ 'ec-toggle__btn--on': clientType === opt.value }"
                @click="switchClientType(opt.value)"
              >
                {{ opt.label }}
              </button>
            </div>
            <!-- Un cliente solo ve lo suyo: el selector es para Transit. -->
            <label v-if="isStaff" class="ec-month">
              <span class="ec-month__label">Cliente</span>
              <select
                v-model="clientId"
                class="ec-month__input ec-select"
                @change="onClientChange"
              >
                <option value="">Todos los clientes</option>
                <option v-for="c in clientOptions" :key="c.id" :value="c.id">
                  {{ c.name }}
                </option>
              </select>
            </label>
          </div>
        </v-flex>

        <!-- ============================ PENDIENTE POR FACTURAR ============ -->
        <v-flex xs12>
          <v-card class="tx-card">
            <div class="tx-toolbar">
              <div class="tx-toolbar__info">
                <span class="tx-chip">
                  <v-icon small class="tx-chip__icon">account_balance</v-icon>
                  {{ pendingRows.length }} clientes con saldo
                </span>
                <span v-if="pendingTotal" class="tx-chip tx-chip--warn">
                  <v-icon small class="tx-chip__icon">payments</v-icon>
                  B/. {{ money(pendingTotal) }} sin facturar
                </span>
              </div>
            </div>
            <v-divider></v-divider>

            <v-card-text class="pa-0">
              <div class="table-wrap">
                <table class="tx-table">
                  <thead>
                    <tr>
                      <th>Cliente</th>
                      <th class="th-center">Trámites</th>
                      <th class="th-right">CIF (USD)</th>
                      <!-- Solo los conceptos del tipo elegido: el otro estado
                           de cuenta tiene los suyos. -->
                      <template v-if="isReembolso">
                        <th class="th-right">Impuestos</th>
                        <th class="th-right">
                          <span :title="OTROS_GASTOS_HINT">
                            Otros gastos por su cuenta
                          </span>
                        </th>
                      </template>
                      <th v-else class="th-right">Servicios aduanales</th>
                      <th class="th-right">Total a facturar</th>
                      <th class="th-actions ec-sticky">Emitir</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in pendingRows" :key="row.clientId">
                      <td class="tx-cell--strong">{{ row.clientName }}</td>
                      <td class="th-center">{{ row.tramites }}</td>
                      <td class="th-right tx-nowrap">
                        {{ money(row.cifTotal) }}
                      </td>
                      <template v-if="isReembolso">
                        <td class="th-right tx-nowrap">
                          <span v-if="row.taxTotal">
                            {{ money(row.taxTotal) }}
                            <span class="ec-count">{{ row.taxCount }}</span>
                          </span>
                          <span v-else class="tx-muted">—</span>
                        </td>
                        <td class="th-right tx-nowrap">
                          <span v-if="row.reimbursementTotal">
                            {{ money(row.reimbursementTotal) }}
                            <span class="ec-count">{{
                              row.reimbursementCount
                            }}</span>
                          </span>
                          <span v-else class="tx-muted">—</span>
                        </td>
                      </template>
                      <td v-else class="th-right tx-nowrap">
                        <span v-if="row.serviceTotal">
                          {{ money(row.serviceTotal) }}
                          <span class="ec-count">{{ row.serviceCount }}</span>
                        </span>
                        <span v-else class="tx-muted">—</span>
                      </td>
                      <td class="th-right tx-nowrap ec-total">
                        <strong>{{ money(row.total) }}</strong>
                      </td>
                      <td class="th-actions ec-sticky">
                        <div class="ec-emit">
                          <v-tooltip bottom>
                            <!-- El span recibe el hover: un botón deshabilitado
                                 no dispara eventos y el aviso no saldría. -->
                            <template slot="activator">
                              <span class="ec-btn-wrap">
                                <button
                                  class="ec-btn"
                                  :disabled="!!row.activeStatement"
                                  @click="openCreate(row)"
                                >
                                  <v-icon small class="ec-btn__icon"
                                    >post_add</v-icon
                                  >
                                  Generar
                                </button>
                              </span>
                            </template>
                            <span>{{ emitTip(row) }}</span>
                          </v-tooltip>
                          <!-- Un estado de cuenta por cliente, tipo y mes: si
                               ya hay uno vigente, se abre ese. -->
                          <button
                            v-if="row.activeStatement"
                            type="button"
                            class="ec-link"
                            @click="openDetail(row.activeStatement)"
                          >
                            Ya emitido:
                            <strong>{{ row.activeStatement.number }}</strong>
                          </button>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="!loadingPending && pendingRows.length === 0">
                      <td :colspan="pendingCols" class="empty-state">
                        {{ pendingEmptyText }}
                      </td>
                    </tr>
                    <tr v-if="loadingPending">
                      <td :colspan="pendingCols" class="empty-state">
                        Cargando...
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>

        <!-- ============================ EMITIDOS ========================== -->
        <v-flex xs12>
          <v-card class="tx-card">
            <div class="tx-toolbar">
              <div class="tx-toolbar__info">
                <span class="tx-chip">
                  <v-icon small class="tx-chip__icon">receipt_long</v-icon>
                  {{ meta.total }} estados de cuenta &middot; {{ kindTitle }}
                </span>
                <v-tooltip bottom v-if="porCobrar">
                  <template slot="activator">
                    <span class="tx-chip tx-chip--danger">
                      <span class="light light--red light--chip"></span>
                      B/. {{ money(porCobrar) }} por cobrar
                    </span>
                  </template>
                  <span
                    >Suma de los que aún no están pagados en esta página</span
                  >
                </v-tooltip>
              </div>
              <div class="tx-toolbar__actions">
                <div class="ec-toggle" role="group" aria-label="Filtrar estado">
                  <button
                    v-for="opt in statusFilters"
                    :key="opt.value"
                    class="ec-toggle__btn"
                    :class="{ 'ec-toggle__btn--on': status === opt.value }"
                    @click="switchStatus(opt.value)"
                  >
                    {{ opt.label }}
                  </button>
                </div>
                <v-tooltip bottom>
                  <template slot="activator">
                    <button
                      class="ec-btn"
                      :disabled="downloading || !statements.length"
                      @click="onDownload"
                    >
                      <v-icon small class="ec-btn__icon">download</v-icon>
                      {{ downloading ? 'Preparando…' : 'Descargar' }}
                    </button>
                  </template>
                  <span>
                    Zip con el Excel (estados de cuenta, cuadro de KPI y detalle
                    por trámite) y los documentos adjuntos
                  </span>
                </v-tooltip>
              </div>
            </div>
            <v-divider></v-divider>

            <v-card-text class="pa-0">
              <div class="table-wrap">
                <table class="tx-table">
                  <thead>
                    <tr>
                      <th class="th-center">Estado</th>
                      <th>Número</th>
                      <th>Cliente</th>
                      <!-- Con un mes elegido todos los renglones son de ese
                           mes: la columna sobra y le quita ancho a la tabla. -->
                      <th v-if="!period">Periodo</th>
                      <th>Emitido</th>
                      <th>Vence</th>
                      <template v-if="isReembolso">
                        <th class="th-right">Impuestos</th>
                        <th class="th-right">
                          <span :title="OTROS_GASTOS_HINT">
                            Otros gastos por su cuenta
                          </span>
                        </th>
                      </template>
                      <th v-else class="th-right">Servicios aduanales</th>
                      <th class="th-right">Total</th>
                      <th class="th-right">Pagado</th>
                      <th class="th-center">Docs.</th>
                      <th class="th-actions ec-col-action ec-sticky">
                        Detalle
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="st in statements"
                      :key="st.id"
                      class="row-clickable"
                      :class="{ 'is-retired': st.status === 'VOID' }"
                      @click="openDetail(st)"
                    >
                      <!-- Semáforo: rojo pendiente, ámbar abonado, verde pagado -->
                      <td class="th-center">
                        <v-tooltip bottom>
                          <template slot="activator">
                            <span class="light" :class="lightOf(st)"></span>
                          </template>
                          <span>{{ statusText(st) }}</span>
                        </v-tooltip>
                      </td>
                      <td class="tx-cell--strong tx-nowrap">
                        {{ st.number }}
                        <!-- Los de antes de separarlos mezclaban los tres
                             conceptos: sus columnas de aquí no suman el total. -->
                        <span
                          v-if="isLegacy(st)"
                          class="ec-legacy"
                          title="Mezclaba el reembolso de gastos operativos y los servicios aduanales: el total incluye los dos"
                        >
                          formato anterior
                        </span>
                      </td>
                      <td>{{ st.clientName || '—' }}</td>
                      <!-- El periodo es un mes; los estados viejos (antes de
                           separarlos por tipo) solo tienen el rango. -->
                      <td v-if="!period" class="tx-nowrap">
                        <span v-if="st.period">{{ mesDe(st.period) }}</span>
                        <span v-else-if="st.periodFrom">
                          {{ dateOf(st.periodFrom) }} al
                          {{ dateOf(st.periodTo) }}
                        </span>
                        <span v-else class="tx-muted">—</span>
                      </td>
                      <td class="tx-nowrap">{{ dateOf(st.issuedAt) }}</td>
                      <td class="tx-nowrap">
                        <span :class="{ 'ec-overdue': isOverdue(st) }">
                          {{ dateOf(st.dueDate) }}
                        </span>
                      </td>
                      <template v-if="isReembolso">
                        <td class="th-right tx-nowrap">
                          {{ money(st.taxTotal) }}
                        </td>
                        <td class="th-right tx-nowrap">
                          {{ money(st.reimbursementTotal) }}
                        </td>
                      </template>
                      <td v-else class="th-right tx-nowrap">
                        {{ money(st.serviceTotal) }}
                      </td>
                      <td class="th-right tx-nowrap ec-total">
                        <strong>{{ money(st.total) }}</strong>
                      </td>
                      <td class="th-right tx-nowrap">
                        <span v-if="st.paidAmount">{{
                          money(st.paidAmount)
                        }}</span>
                        <span v-else class="tx-muted">—</span>
                      </td>
                      <td class="th-center">
                        <span v-if="st.documents" class="ec-count">
                          {{ st.documents }}
                        </span>
                        <span v-else class="tx-muted">—</span>
                      </td>
                      <td
                        class="th-actions ec-col-action ec-sticky"
                        @click.stop
                      >
                        <v-tooltip bottom>
                          <template slot="activator">
                            <button
                              class="doc-btn"
                              :class="{ 'doc-btn--done': st.status === 'PAID' }"
                              @click="openDetail(st)"
                            >
                              <v-icon small>list_alt</v-icon>
                              Ver
                            </button>
                          </template>
                          <span>
                            Detalle por trámite, cuadro de KPI, documentos y
                            registro del pago
                          </span>
                        </v-tooltip>
                      </td>
                    </tr>
                    <tr v-if="!loadingList && statements.length === 0">
                      <td :colspan="issuedCols" class="empty-state">
                        {{ issuedEmptyText }}
                      </td>
                    </tr>
                    <tr v-if="loadingList">
                      <td :colspan="issuedCols" class="empty-state">
                        Cargando...
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>

        <v-flex xs12 v-if="pages > 1">
          <div class="list-footer">
            <div class="list-footer__count">
              Mostrando {{ statements.length }} de {{ meta.total }}
            </div>
            <v-pagination
              v-model="page"
              :length="pages"
              :total-visible="7"
              color="#254f87"
              @input="loadStatements"
            ></v-pagination>
          </div>
        </v-flex>
      </v-layout>
    </v-container>

    <!-- 1200 en los dos diálogos: con el N° y la fecha Alegra la tabla no
         cabía y se cortaba a la derecha (lo reportó el cliente). Si aun así
         no cabe, la tabla se desplaza dentro del diálogo. -->
    <v-dialog
      max-width="1200"
      v-if="create.data"
      v-model="create.show"
      persistent
    >
      <statement-create
        :key="create.key"
        :client="create.data"
        :kind="kind"
        :period="period"
        v-on:close-create="create.show = false"
        v-on:created="onCreated"
      ></statement-create>
    </v-dialog>

    <v-dialog max-width="1200" v-if="detail.id" v-model="detail.show">
      <statement-detail
        :key="detail.key"
        :statement-id="detail.id"
        v-on:close-statement="detail.show = false"
        v-on:changed="reloadAll"
      ></statement-detail>
    </v-dialog>

    <v-snackbar
      v-model="snack.show"
      :timeout="4000"
      :color="snack.color"
      right
      top
    >
      {{ snack.message }}
      <v-btn flat dark @click="snack.show = false">Cerrar</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import moment from 'moment'
import SessionHelper from '@/helpers/SessionHelper'
import statementCreate from './../dialog/DialogStatementCreate'
import statementDetail from './../dialog/DialogStatement'

// Sin depender del locale de moment: la app no lo fija y el mes se lee en
// español en la pantalla, el correo y el Excel.
const MESES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
]

// Mismo criterio que el detalle: se listan los roles de cliente, no los de
// Transit, para que un rol interno nuevo herede lo correcto.
const CLIENT_ROLES = ['LAB', 'DIST', 'CLI']

const byName = (a, b) => String(a.name).localeCompare(String(b.name), 'es')

export default {
  components: {
    'statement-create': statementCreate,
    'statement-detail': statementDetail,
  },
  data() {
    return {
      // Tipo de estado de cuenta. El cliente pidió que el reembolso de gastos
      // operativos y los servicios aduanales se emitan por separado.
      kind: 'REEMBOLSO',
      // Los botones llevan el nombre completo del documento: es el mismo que
      // el cliente ve en el correo y en el Excel.
      kinds: [
        { value: 'REEMBOLSO', label: 'Reembolso de gastos operativos' },
        { value: 'SERVICIOS', label: 'Servicios aduanales' },
      ],
      // Mes de la factura Alegra (AAAA-MM). Se arranca en el mes en curso;
      // vacío trae todos los meses.
      period: moment().format('YYYY-MM'),
      clientType: 'DIST',
      clientTypes: [
        { value: 'DIST', label: 'Distribuidores' },
        { value: 'LAB', label: 'Laboratorios' },
      ],
      // Cliente del tipo elegido; vacío = todos.
      clientId: '',
      // Catálogo para el selector de cliente, por tipo.
      clients: { DIST: [], LAB: [] },
      // Respaldo si el catálogo no carga: los clientes vistos en la lista.
      seenClients: {},
      status: '',
      statusFilters: [
        { value: '', label: 'Todos' },
        { value: 'ISSUED', label: 'Por cobrar' },
        { value: 'PARTIAL', label: 'Abonados' },
        { value: 'PAID', label: 'Pagados' },
        { value: 'VOID', label: 'Anulados' },
      ],
      pending: [],
      loadingPending: true,
      statements: [],
      meta: { total: 0 },
      loadingList: true,
      page: 1,
      rpp: 25,
      create: { show: false, data: null, key: 0 },
      detail: { show: false, id: null, key: 0 },
      downloading: false,
      snack: { show: false, color: 'info', message: '' },
      OTROS_GASTOS_HINT: 'Notaría, APA, bomberos, almacenaje…',
    }
  },
  created() {
    this.loadClients()
    this.reloadAll()
  },
  computed: {
    isStaff() {
      return CLIENT_ROLES.indexOf(SessionHelper.role) === -1
    },
    isReembolso() {
      return this.kind === 'REEMBOLSO'
    },
    // El nombre del documento, en las tres formas que piden los textos. Es el
    // mismo que usan el backend (KIND_LABEL), el correo y el Excel; tenerlo
    // aquí en un solo lugar evita que cada pantalla lo escriba distinto.
    kindTitle() {
      return this.isReembolso
        ? 'Reembolso de gastos operativos'
        : 'Servicios aduanales'
    },
    kindFrase() {
      return this.isReembolso
        ? 'reembolso de gastos operativos'
        : 'servicios aduanales'
    },
    kindPlural() {
      return this.isReembolso
        ? 'reembolsos de gastos operativos'
        : 'servicios aduanales'
    },
    clientOptions() {
      const catalog = this.clients[this.clientType] || []
      if (catalog.length) return catalog
      return Object.keys(this.seenClients)
        .map(k => this.seenClients[k])
        .filter(c => c.clientType === this.clientType)
        .sort(byName)
    },
    clientName() {
      const found = this.clientOptions.find(
        c => String(c.id) === String(this.clientId)
      )
      return found ? found.name : ''
    },
    // El pendiente llega por cliente: el filtro de cliente se aplica aquí.
    pendingRows() {
      if (!this.clientId) return this.pending
      return this.pending.filter(
        r => String(r.clientId) === String(this.clientId)
      )
    },
    // Columnas visibles de cada tabla: cambian con el tipo y el mes.
    pendingCols() {
      return this.isReembolso ? 7 : 6
    },
    issuedCols() {
      return 9 + (this.period ? 0 : 1) + (this.isReembolso ? 2 : 1)
    },
    pendingTotal() {
      return this.pendingRows.reduce((acc, r) => acc + (r.total || 0), 0)
    },
    porCobrar() {
      return this.statements
        .filter(s => s.status === 'ISSUED' || s.status === 'PARTIAL')
        .reduce((acc, s) => acc + (s.total - (s.paidAmount || 0)), 0)
    },
    pages() {
      return Math.ceil(this.meta.total / this.rpp) || 0
    },
    enMes() {
      return this.period ? ` en ${this.mesDe(this.period)}` : ''
    },
    pendingEmptyText() {
      const quien = this.clientName
        ? `${this.clientName} no tiene`
        : 'Ningún cliente tiene'
      return `${quien} ${this.kindPlural} pendientes de facturar${this.enMes}.`
    },
    issuedEmptyText() {
      if (this.status || this.clientId) {
        return `Ningún estado de cuenta de ${this.kindFrase}${
          this.enMes
        } con estos filtros.`
      }
      return `Todavía no se ha emitido ningún estado de cuenta de ${
        this.kindFrase
      }${this.enMes}.`
    },
    // Filtros de la pantalla que valen para la lista y la descarga. Sin
    // valor no se manda la clave: el query string serializaba `undefined`
    // como texto.
    scopeParams() {
      return {
        kind: this.kind,
        clientType: this.clientType,
        ...(this.period ? { period: this.period } : {}),
        ...(this.clientId ? { clientId: this.clientId } : {}),
        ...(this.status ? { status: this.status } : {}),
      }
    },
  },
  methods: {
    money(value) {
      const n = parseFloat(value)
      if (Number.isNaN(n)) return '0.00'
      return n.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
    dateOf(value) {
      return value ? moment(value).format('DD/MM/YYYY') : '—'
    },
    // Los emitidos antes de separarlos por tipo no tienen mes.
    isLegacy(st) {
      return !!st.legacy || !st.period
    },
    // Un abono no es ni lo uno ni lo otro: pintarlo rojo negaría un pago que
    // sí entró, y verde daría por saldado lo que aún se debe.
    lightOf(st) {
      if (st.status === 'PAID') return 'light--green'
      if (st.status === 'PARTIAL') return 'light--amber'
      if (st.status === 'VOID') return 'light--grey'
      return 'light--red'
    },
    statusText(st) {
      if (st.status === 'PAID') return 'Realizado: el cliente ya canceló'
      if (st.status === 'PARTIAL') {
        return `Abonado: falta B/. ${this.money(
          st.total - (st.paidAmount || 0)
        )}`
      }
      if (st.status === 'VOID') {
        return 'Anulado: sus trámites volvieron a quedar pendientes'
      }
      return 'Pendiente: el cliente no ha cancelado'
    },
    isOverdue(st) {
      if (!st.dueDate || st.status === 'PAID' || st.status === 'VOID') {
        return false
      }
      return moment(st.dueDate).isBefore(moment(), 'day')
    },
    notify(message, color = 'info') {
      this.snack = { show: true, color, message }
    },
    // "Septiembre 2026" a partir de AAAA-MM.
    mesDe(period) {
      const m = /^(\d{4})-(\d{2})$/.exec(period || '')
      if (!m) return ''
      return `${MESES[parseInt(m[2], 10) - 1]} ${m[1]}`
    },
    // Sin mes se puede revisar el detalle, pero el estado de cuenta se emite
    // por mes. Con uno vigente no se emite otro: se anula y se vuelve a
    // emitir con todos los trámites.
    emitTip(row) {
      if (row.activeStatement) {
        return 'Anula el estado emitido para volver a emitirlo con todos los trámites'
      }
      if (!this.period) {
        return 'Revisa el detalle. Para emitir, elige un mes arriba'
      }
      return `Revisa el detalle y emite el estado de cuenta de ${
        this.kindFrase
      } de este cliente para ${this.mesDe(this.period)}`
    },
    switchKind(value) {
      if (this.kind === value) return
      this.kind = value
      this.page = 1
      this.reloadAll()
    },
    onPeriodChange() {
      this.page = 1
      this.reloadAll()
    },
    // El cliente elegido es de un tipo: al cambiar de tipo se suelta.
    switchClientType(value) {
      if (this.clientType === value) return
      this.clientType = value
      this.clientId = ''
      this.page = 1
      this.reloadAll()
    },
    onClientChange() {
      this.page = 1
      this.loadStatements()
    },
    switchStatus(value) {
      if (this.status === value) return
      this.status = value
      this.page = 1
      this.loadStatements()
    },
    loadClients() {
      if (!this.isStaff) return
      const load = (action, type) =>
        this.$store.dispatch(action, {}).then(response => {
          this.clients[type] = ((response && response.items) || [])
            .map(c => ({ id: c.id, name: c.name, clientType: type }))
            .sort(byName)
        })
      load('distributors/getAll', 'DIST')
      load('laboratories/getAll', 'LAB')
    },
    rememberClients(rows) {
      rows.forEach(st => {
        const id = st.clientType === 'LAB' ? st.labId : st.distId
        if (!id || !st.clientName) return
        this.$set(this.seenClients, `${st.clientType}:${id}`, {
          id,
          name: st.clientName,
          clientType: st.clientType,
        })
      })
    },
    loadPending() {
      this.loadingPending = true
      this.$store
        .dispatch('accountStatement/getPending', {
          clientType: this.clientType,
          kind: this.kind,
          // Sin mes no se manda la clave: el query string serializaba
          // `undefined` como texto.
          ...(this.period ? { period: this.period } : {}),
        })
        .then(rows => {
          this.pending = rows || []
        })
        .finally(() => {
          this.loadingPending = false
        })
    },
    loadStatements() {
      this.loadingList = true
      this.$store
        .dispatch('accountStatement/getAll', {
          ...this.scopeParams,
          limit: this.rpp,
          offset: (this.page - 1) * this.rpp,
        })
        .then(response => {
          this.statements = (response && response.rows) || []
          this.meta = { total: (response && response.total) || 0 }
          this.rememberClients(this.statements)
        })
        .finally(() => {
          this.loadingList = false
        })
    },
    reloadAll() {
      this.loadPending()
      this.loadStatements()
    },
    openCreate(row) {
      this.create = { show: true, data: row, key: this.create.key + 1 }
    },
    onCreated(statement) {
      this.create.show = false
      this.notify(`Estado de cuenta ${statement.number} emitido.`, 'success')
      this.reloadAll()
      this.openDetail(statement)
    },
    openDetail(st) {
      this.detail = { show: true, id: st.id, key: this.detail.key + 1 }
    },
    // Respeta los filtros de la lista (tipo, mes, cliente y estado). Sale por
    // socket como el resto de las descargas; el layout abre el archivo
    // cuando está listo.
    onDownload() {
      this.downloading = true
      this.$socket.emit('exportAccountStatements', {
        ...this.scopeParams,
        token: SessionHelper.token,
      })
    },
  },
  sockets: {
    accountStatementsFile(response) {
      this.downloading = false
      this.notify(
        `${response.statements} estado(s) de cuenta y ` +
          `${response.documents} documento(s) en el zip.`,
        'success'
      )
    },
    accountStatementsFileError(message) {
      this.downloading = false
      this.notify(message || 'No se pudo generar la descarga.', 'error')
    },
  },
}
</script>

<style scoped src="./../transit/transit-list.css"></style>

<style scoped>
.row-clickable {
  cursor: pointer;
}
.row-clickable:hover {
  background: #f1f5f9;
}
/* Un anulado se conserva para auditar, pero ya no se cobra: se apaga. */
.tx-table tr.is-retired td {
  color: #94a3b8;
}
.tx-table tr.is-retired .tx-cell--strong,
.tx-table tr.is-retired strong {
  color: #64748b;
}

/* Si la tabla no cabe se desplaza a lo ancho dentro de la tarjeta, con la
   barra siempre visible (en Mac se esconde sola y parecía cortada) y la
   acción de la fila fija a la derecha. */
.table-wrap {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
}
.table-wrap::-webkit-scrollbar {
  height: 8px;
}
.table-wrap::-webkit-scrollbar-track {
  background: #f1f5f9;
}
.table-wrap::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}
.table-wrap::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
/* La celda fija hereda el fondo de su fila (también el del hover): sin
   fondo propio, lo que pasa por debajo se vería a través. */
.tx-table tbody tr {
  background: #ffffff;
}
.ec-sticky {
  position: sticky;
  right: 0;
  z-index: 1;
  background: inherit;
  box-shadow: inset 1px 0 0 #eef2f7;
}
.tx-table .ec-col-action {
  width: 1%;
  white-space: nowrap;
}

.light {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  vertical-align: middle;
}
.light--red {
  background: #dc2626;
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.18);
}
.light--green {
  background: #15803d;
  box-shadow: 0 0 0 3px rgba(21, 128, 61, 0.18);
}
.light--amber {
  background: #b45309;
  box-shadow: 0 0 0 3px rgba(180, 83, 9, 0.18);
}
.light--grey {
  background: #94a3b8;
  box-shadow: 0 0 0 3px rgba(148, 163, 184, 0.18);
}
/* Dentro de un chip el halo no cabe: se queda sólo el punto. */
.light--chip {
  width: 9px;
  height: 9px;
  box-shadow: none;
  margin-right: 6px;
}

.tx-chip--danger {
  background: rgba(220, 38, 38, 0.12);
  color: #991b1b;
}
.tx-chip--warn {
  background: #fffbeb;
  color: #92400e;
}
.tx-chip--warn .tx-chip__icon {
  color: #b45309 !important;
}

/* La cabecera lleva a la derecha el selector de tipo: es la decisión que
   cambia todo lo que hay debajo. */
.ec-hero {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px 24px;
}
.ec-toggle--kind .ec-toggle__btn {
  height: 36px;
  padding: 0 20px;
  font-size: 13px;
}

/* Mes, tipo de cliente y cliente: el alcance de las dos tarjetas. */
.ec-scope {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 16px;
  margin-top: 12px;
}
.ec-month {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.ec-month__label {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}
.ec-month__input {
  height: 30px;
  padding: 0 8px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #ffffff;
  color: #1e293b;
  font-size: 12px;
  font-weight: 600;
  outline: none;
}
.ec-month__input:focus {
  border-color: #254f87;
  box-shadow: 0 0 0 3px rgba(37, 79, 135, 0.14);
}
/* El select nativo no trae flecha en este reset: se dibuja una. */
.ec-select {
  min-width: 200px;
  max-width: 320px;
  padding-right: 28px;
  appearance: none;
  -webkit-appearance: none;
  background-image: linear-gradient(45deg, transparent 50%, #64748b 50%),
    linear-gradient(135deg, #64748b 50%, transparent 50%);
  background-position: calc(100% - 14px) 50%, calc(100% - 9px) 50%;
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
  cursor: pointer;
}

/* Selector de segmentos: dos filtros que se leen de un vistazo, sin abrir
   un desplegable para cambiar de vista. */
.ec-toggle {
  display: inline-flex;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
}
.ec-toggle__btn {
  padding: 0 14px;
  height: 30px;
  border: 0;
  background: transparent;
  color: #475569;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease;
}
.ec-toggle__btn + .ec-toggle__btn {
  border-left: 1px solid #e2e8f0;
}
.ec-toggle__btn:hover {
  background: #f1f5f9;
}
.ec-toggle__btn--on {
  background: #254f87;
  color: #ffffff;
}
.ec-toggle__btn--on:hover {
  background: #1e4270;
}

.ec-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  margin-left: 6px;
  border-radius: 999px;
  background: #e2e8f0;
  color: #475569;
  font-size: 11px;
  font-weight: 700;
}

/* Los estados de cuenta de antes de separarlos por tipo. */
.ec-legacy {
  display: block;
  width: max-content;
  margin-top: 3px;
  padding: 1px 8px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 11px;
  font-weight: 600;
}

.ec-total {
  background: #f8fafc;
}

/* Una fecha vencida es la única cosa de la tabla que pide acción hoy. */
.ec-overdue {
  color: #991b1b;
  font-weight: 700;
}

/* Emitir y, debajo, el estado de cuenta que ya ocupa ese mes. */
.ec-emit {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}
.ec-btn-wrap {
  display: inline-block;
}
.ec-link {
  padding: 0;
  border: 0;
  background: transparent;
  color: #254f87;
  font-size: 12px;
  white-space: nowrap;
  cursor: pointer;
}
.ec-link:hover {
  text-decoration: underline;
}

/* Emitir es la acción principal de la fila: se gana el color. */
.ec-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 28px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid #254f87;
  background: #254f87;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.18s ease;
}
.ec-btn .v-icon {
  color: #ffffff !important;
  font-size: 15px !important;
}
.ec-btn:hover:not(:disabled) {
  background: #1e4270;
  border-color: #1e4270;
}
/* Sin eventos en el botón apagado, el hover llega al span que abre el
   aviso de por qué no se puede. */
.ec-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  pointer-events: none;
}
.ec-btn-wrap:hover {
  cursor: not-allowed;
}

.doc-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 28px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #475569;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.18s ease;
}
.doc-btn .v-icon {
  color: #64748b !important;
  font-size: 15px !important;
}
.doc-btn:hover {
  background: #f1f5f9;
  border-color: #94a3b8;
}
.doc-btn--done {
  border-color: #bbf7d0;
  background: #f0fdf4;
  color: #14532d;
}
.doc-btn--done .v-icon {
  color: #15803d !important;
}
</style>
